import _ from 'lodash';
import Dinero from 'dinero.js';
import type { Dinero as DineroType } from 'dinero.js';
import { formatCurrency } from '@/data/api/products/helpers';
import { instance } from '@/data/api/products/axios';

import PromiseCache from '@/helpers/promisecache';
import { PriceCollection } from '@/products';

export enum Currency {
  SEK = 'SEK',
  NOK = 'NOK',
  EUR = 'EUR',
}

export interface Price {
  netPrice: number;
  salesPrice: number;
  discount: number;
  lowestQuantityLimitBasicU_M: number;
  salesPriceUnitOfMeasure: string;
  netPriceUnitOfMeasure: string; // Internal field added by our API
  currency: string;
  priceList: string;
  priceListOrigin: string;
  blanketAgreementNumber: string;
  price: number;
  price_total: number;
  isEmpty: boolean;
}
export interface LinePrice {
  lineAmount: number;
  salesPrice: number;
  currency: string;
  priceList: string;
  orderDate: string;
  salesPriceUnitOfMeasure: string;
  salesPriceQuantity: string;
  priceUnitOfMeasure: string; // Internal field added by our API
  priceQuantity: string; // Internal field added by our API
  warehouse: string;
  statusDiscount1: string;
  statusDiscount2: string;
  statusDiscount3: string;
  statusDiscount4: string;
  statusDiscount5: string;
  statusDiscount6: string;
  discountText1: string;
  discountText2: string;
  discountText3: string;
  discountText4: string;
  discountText5: string;
  discountText6: string;
  discount1: string;
  discount2: string;
  discount3: string;
  discount4: string;
  discount5: string;
  discount6: string;
}

export enum PriceType {
  Sales = 'sales',
  Net = 'net',
  M2 = 'm2',
  Line = 'line',
  None = '',
}

export interface PriceObject {
  sales: Price | null;
  net: Price | null;
  m2: LinePrice | null;
  line: LinePrice | null;
}

export interface PriceAttributes {
  artnr: string;
  customer: string;
  warehouse: string;
  region: string;
  quantity: number;
  requestQuantity?: number;
  unit?: string;
  address?: string;
  unique?: string; // Generally not used, but can be used to differentiate between different price collections
  salespriceUnit?: string;
  date?: string; // The selected delivery date as a string
  priceList?: string;
  priceListOrigin?: string;
}

// We store prices in a map to avoid issues with cloning the PriceManager while it's fetching prices
// Note: This will cause a memory leak as the map isn't cleared until a page refresh. But we should be fine for now.
class PriceStore {
  private prices: Map<
    string,
    {
      TTL: number;
      value: PriceObject;
    }
  >;
  private scheduledForDeletion: Set<string>;

  constructor() {
    this.prices = new Map();
    this.scheduledForDeletion = new Set();

    setInterval(() => {
      this.scheduledForDeletion.forEach((key) => {
        // console.debug('Deleting key', key);
        this.prices.delete(key);
      });
      this.scheduledForDeletion.clear();
    }, 60 * 1000);
  }

  set(key: string, value: PriceObject): void {
    this.scheduledForDeletion.delete(key);
    this.prices.set(key, {
      TTL: Date.now(),
      value,
    });
  }

  get(key: string): PriceObject | undefined {
    this.scheduledForDeletion.delete(key);
    return this.prices.get(key)?.value;
  }

  delete(key: string): void {
    const p = this.prices.get(key);
    if (!p) {
      // console.warn('Deleted was called, but pricestore already deleted', key);
      return;
    }
    // Check if we should delete immediately to prevent stale data
    if (Date.now() - p.TTL > 120 * 1000) {
      // console.debug('Deleting key immediately', key);
      this.prices.delete(key);
    } else {
      this.scheduledForDeletion.add(key);
    }
  }

  has(key: string): boolean {
    return this.prices.has(key);
  }

  clear(): void {
    this.prices.clear();
  }
}
declare global {
  interface Window {
    _priceManagerPriceStore: PriceStore;
  }
}

if (!window._priceManagerPriceStore) {
  window._priceManagerPriceStore = new PriceStore();
}

const isNullish = (value: any): boolean => value === undefined || value === null || value === '';
const isEqualNonStrict = (a: Record<string, any>, b: Record<string, any>): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const aVal = a[key];
    const bVal = b[key];
    if (isNullish(aVal) && isNullish(bVal)) continue;
    if (!_.isEqual(aVal, bVal)) return false;
  }
  return true;
};

export class PriceManager {
  readonly isVoid: boolean = false;
  private _fetching: Promise<any> | null = null;
  private fetcher: (price_manager: PriceManager, type?: PriceType) => Promise<PriceCollection | null>;
  private attributes: PriceAttributes = {
    artnr: '',
    address: '',
    customer: '',
    warehouse: '',
    region: '',
    unit: '',
    quantity: 0,
    requestQuantity: 0,
    salespriceUnit: '',
    date: '',
    priceList: '',
    priceListOrigin: '',
  };

  private initial: {
    prices: PriceObject | undefined;
    attributes: PriceAttributes;
  };
  // private initial_prices: PriceObject | undefined; // The price this pricemanager was initialized with (Note: if attributes were changed, these will not reflect that unless specifically re-initialized)

  private get key(): string {
    return `${this.artnr}:${this.address ?? ''}:${this.customer}:${this.warehouse}:${this.region}:${this.requestQuantity ?? ''}:${this.quantity}:${this.unit}:${this.attributes.unique ?? ''}:${this.salespriceUnit ?? ''}:${this.attributes.date ?? ''}:${this.attributes.priceList ?? ''}:${this.attributes.priceListOrigin ?? ''}`;
  }

  private set_prices(value: PriceObject) {
    window._priceManagerPriceStore.set(this.key, value);
  }
  private get_prices(initial_price?: boolean): PriceObject {
    if (initial_price && this.initial.prices) {
      return this.initial.prices;
    }

    const prices = window._priceManagerPriceStore.get(this.key);
    if (prices) {
      return prices;
    }

    window._priceManagerPriceStore.set(this.key, {
      sales: null,
      net: null,
      m2: null,
      line: null,
    });

    return window._priceManagerPriceStore.get(this.key)!;
  }

  awaitFetching(): Promise<void> {
    if (this._fetching) {
      return this._fetching.then(() => {});
    } else {
      return Promise.resolve();
    }
  }

  get loading(): boolean {
    return this._fetching !== null;
  }

  get needsQuote(): boolean {
    return !this?.loading && !this?.exists(PriceType.Net) && !this?.exists(PriceType.Sales);
  }

  set artnr(value: string) {
    this.attributes.artnr = value;
  }
  get artnr(): string {
    return this.attributes.artnr;
  }

  set address(value: string) {
    this.attributes.address = value;
  }
  get address(): string | undefined {
    return this.attributes.address;
  }

  set customer(value: string) {
    this.attributes.customer = value;
  }
  get customer(): string {
    return this.attributes.customer;
  }

  set warehouse(value: string) {
    this.attributes.warehouse = value;
  }
  get warehouse(): string {
    return this.attributes.warehouse;
  }

  set region(value: string) {
    this.attributes.region = value;
  }
  get region(): string {
    return this.attributes.region;
  }

  set quantity(value: number) {
    this.attributes.quantity = value;
  }
  get quantity(): number {
    return this.attributes.quantity;
  }

  set requestQuantity(requestQuantity: number) {
    this.attributes.requestQuantity = requestQuantity;
  }
  get requestQuantity(): number | undefined {
    return this.attributes.requestQuantity;
  }

  set unit(value: string) {
    this.attributes.unit = value;
  }
  get unit(): string | undefined {
    return this.attributes.unit;
  }

  set salespriceUnit(value: string) {
    this.attributes.salespriceUnit = value;
  }
  get salespriceUnit(): string | undefined {
    return this.attributes.salespriceUnit;
  }

  /**
   *
   * Supplying the prices is optional. If not supplied, the prices will be fetched from the API.
   *
   * @param attributes  The attributes for the price collection
   * @param prices  The prices for the price collection
   */
  constructor(
    attributes: PriceAttributes,
    prices?: PriceObject,
    fetcher?: (price_manager: PriceManager, type?: PriceType) => Promise<PriceCollection | null>
  ) {
    this.attributes = attributes;
    this.initial = {
      prices: undefined,
      attributes: attributes,
    };

    // console.log(
    //   'prices',
    //   !!prices,
    //   'not null',
    //   Object.values(prices).some((price) => price !== null)
    // );
    this.fetcher =
      fetcher ??
      function (price_manager: PriceManager, type?: PriceType): Promise<PriceCollection | null> {
        switch (type) {
          // TODO: Implement support for just updating a single price type, if needed
          default:
            return instance
              .get(`/pricecollection/${price_manager.artnr}`, {
                params: {
                  address: price_manager.address,
                  customer: price_manager.customer,
                  warehouse: price_manager.warehouse,
                  region: price_manager.region,
                  quantity: price_manager.quantity,
                  requestQuantity: price_manager.requestQuantity,
                  unit: price_manager.unit,
                  single: true,
                  salespriceUnit: price_manager.salespriceUnit,
                  date: price_manager.attributes.date,
                  priceList: price_manager.attributes.date ? price_manager.attributes.priceList : undefined,
                  priceListOrigin: price_manager.attributes.date ? price_manager.attributes.priceListOrigin : undefined,
                },
              })
              .then((response) => {
                if (response?.status === 200) {
                  return response.data;
                }
                return null;
              });
        }
      };

    if (prices && (Object.values(prices) as Price[]).some((price) => price !== null)) {
      this.initial.prices = prices;
      this.set_prices(prices);
    }

    this.populate();
  }

  // Sets all new attributes and re-populates if they have changed
  set_attributes(attributes: PriceAttributes) {
    const has_changed = !isEqualNonStrict(attributes, this.attributes);
    if (has_changed) {
      this.attributes = { ...attributes };
      this.populate();
    }
  }

  update_attributes(updates: Partial<PriceAttributes>): void {
    const attributes = { ...this.attributes };
    Object.entries(updates).forEach(([key, value]) => {
      (attributes as any)[key] = value;
    });

    this.set_attributes(attributes);
  }

  // Cleans up the PriceManager to avoid memory leaks
  destroy(): void {
    window._priceManagerPriceStore.delete(this.key);
  }

  private populate(type?: PriceType): Promise<void> {
    if (!this.artnr) return Promise.resolve(); // If no artnr this is probably a placeholder and we shouldn't fetch
    if (this.initial.prices) {
      const matches_initial = isEqualNonStrict(this.attributes, this.initial.attributes);
      if (matches_initial) {
        this.set_prices(this.initial.prices);
        return Promise.resolve();
      }
    }

    let res: Promise<PriceCollection | null>;
    res = PromiseCache.get(() => this.fetcher(this, type), this.key);

    this._fetching = res;
    return new Promise<void>((resolve) => {
      res
        .then((data) => {
          if (data) {
            switch (type) {
              case PriceType.Sales:
                this.get_prices().sales = data.price_sales;
                break;
              case PriceType.Net:
                this.get_prices().net = data.price_net;
                break;
              case PriceType.M2:
                this.get_prices().m2 = data.price_m2 ?? null;
                break;
              case PriceType.Line:
                this.get_prices().line = data.price_line;
                break;
              default:
                this.set_prices({
                  sales: data.price_sales,
                  net: data.price_net,
                  line: data.price_line,
                  m2: data.price_m2 ?? null,
                });
            }
          }
          resolve();
        })
        .catch((error) => {
          console.error('Error fetching price collection', error);
          resolve();
        })
        .finally(() => {
          console.log('PriceManager: Fetching finished');
          this._fetching = null;
        });
    });
  }

  exists(type: PriceType, initial_price?: boolean): boolean {
    switch (type) {
      case PriceType.Sales:
      case PriceType.Net:
      case PriceType.M2:
      case PriceType.Line:
        break;
      default:
        return false;
    }

    const price = this.get_prices(initial_price);
    switch (type) {
      case PriceType.Sales:
        return price.sales !== null && !price.sales.isEmpty;
      case PriceType.Net:
        return price.net !== null && !price.net.isEmpty;
      case PriceType.M2:
        return price.m2 !== null;
      case PriceType.Line:
        return price.line !== null;
    }
  }

  // Returns the price without the applied discounts
  getBasePrice(type: PriceType, initial_price?: boolean): Pricing {
    let amount: number | undefined;

    const getWithoutDiscount = (amount: number, ...discounts: (string | number | undefined)[]): number => {
      let p = Dinero({
        amount: Math.round(amount * 1000),
        currency: this.getCurrency(type) ?? undefined,
        precision: 3,
      });
      for (let d of discounts) {
        const num = typeof d === 'string' ? parseFloat(d) : d;
        if (!num) continue;
        const divisor = (100 - num) / 100;
        if (!divisor) continue;
        p = p.divide(divisor);
      }

      return p.toUnit();
    };

    switch (type) {
      case PriceType.Net:
        const net = this.get_prices(initial_price).net;
        amount = getWithoutDiscount(net?.netPrice || 0, net?.discount);
        break;
      case PriceType.Sales:
        const sales = this.get_prices(initial_price).sales;
        amount = getWithoutDiscount(sales?.netPrice || 0, sales?.discount);
        break;
      case PriceType.M2:
        const m2 = this.get_prices(initial_price).net;
        amount = m2?.salesPrice;
        // const m2 = this.get_prices(initial_price).m2;
        // amount = getWithoutDiscount(
        //   m2?.salesPrice || 0,
        //   m2?.discount1,
        //   m2?.discount2,
        //   m2?.discount3,
        //   m2?.discount4,
        //   m2?.discount5,
        //   m2?.discount6
        // );
        // amount = m2?.salesPrice;
        break;
      case PriceType.Line:
        const line = this.get_prices(initial_price).net;
        amount = line?.salesPrice;
        // const line = this.get_prices(initial_price).line;
        // amount = getWithoutDiscount(
        //   line?.salesPrice || 0,
        //   line?.discount1,
        //   line?.discount2,
        //   line?.discount3,
        //   line?.discount4,
        //   line?.discount5,
        //   line?.discount6
        // );
        break;
      case PriceType.None:
        return new Pricing(null, '');
      default:
        throw new Error('Invalid price type');
    }

    return new Pricing(amount ?? null, this.getCurrency(type, initial_price));
  }

  getPrice(type: PriceType, initial_price?: boolean): Pricing {
    let amount: number | undefined;
    switch (type) {
      case PriceType.Net:
        amount = this.get_prices(initial_price).net?.netPrice;
        break;
      case PriceType.Sales:
        amount = this.get_prices(initial_price).sales?.salesPrice;
        break;
      case PriceType.M2:
        amount = this.get_prices(initial_price).m2?.salesPrice;
        break;
      case PriceType.Line:
        amount = this.get_prices(initial_price).line?.salesPrice;
        break;
      case PriceType.None:
        return new Pricing(null, '');
      default:
        throw new Error('Invalid price type');
    }

    return new Pricing(amount ?? null, this.getCurrency(type, initial_price));
  }

  getPriceTotal(type: PriceType, initial_price?: boolean): Pricing {
    let amount: number | undefined;
    switch (type) {
      case PriceType.Net:
        amount = this.get_prices(initial_price).net?.price_total;
        break;
      case PriceType.Sales:
        amount = this.get_prices(initial_price).sales?.price_total;
        break;
      case PriceType.M2:
        amount = this.get_prices(initial_price).m2?.lineAmount;
        break;
      case PriceType.Line:
        amount = this.get_prices(initial_price).line?.lineAmount;
        break;
      case PriceType.None:
        return new Pricing(null, '');
      default:
        throw new Error('Invalid price type');
    }

    return new Pricing(amount ?? null, this.getCurrency(type, initial_price));
  }

  getDiscount(
    type: Exclude<PriceType, PriceType.Net | PriceType.Sales>,
    index: 1 | 2 | 3 | 4 | 5 | 6 = 1,
    initial_price?: boolean
  ): { discount: number; text: string; status: string } | null {
    if (index < 1 || index > 6) {
      return null;
    }

    let linePrice: LinePrice | null = null;
    switch (type) {
      case PriceType.M2:
        linePrice = this.get_prices(initial_price).m2;
        break;
      case PriceType.Line:
        linePrice = this.get_prices(initial_price).line;
        break;
      default:
        return null;
    }

    return {
      discount: +(linePrice?.[`discount${index}`] ?? 0),
      text: linePrice?.[`discountText${index}`] ?? '',
      status: linePrice?.[`statusDiscount${index}`] ?? '',
    };
  }

  getAllDiscounts(
    type: Exclude<PriceType, PriceType.Net | PriceType.Sales>,
    initial_price?: boolean
  ): { discount: number; text: string; status: string }[] {
    const discounts: { discount: number; text: string; status: string }[] = [];
    for (let i = 1; i <= 6; i++) {
      const discount = this.getDiscount(type, i as 1 | 2 | 3 | 4 | 5 | 6, initial_price);
      if (discount) {
        discounts.push(discount);
      }
    }

    return discounts;
  }

  getTotalDiscount(type: Exclude<PriceType, PriceType.Net | PriceType.Sales>, initial_price?: boolean): number {
    let remaining = 1;
    for (let i = 1; i <= 6; i++) {
      const discount = this.getDiscount(type, i as 1 | 2 | 3 | 4 | 5 | 6, initial_price);
      if (discount) {
        remaining *= 1 - discount.discount / 100;
      }
    }
    return +((1 - remaining) * 100).toFixed(2);
  }

  getBaseUnit(type: PriceType, initial_price?: boolean): string | null {
    switch (type) {
      case PriceType.Net:
        return (
          this.get_prices(initial_price).net?.netPriceUnitOfMeasure ??
          this.get_prices(initial_price).net?.salesPriceUnitOfMeasure ??
          null
        );
      case PriceType.Sales:
        return this.get_prices(initial_price).sales?.salesPriceUnitOfMeasure ?? null;
      case PriceType.M2:
        return 'm2';
      case PriceType.Line:
        return (
          this.get_prices(initial_price).m2?.priceUnitOfMeasure ??
          this.get_prices(initial_price).line?.salesPriceUnitOfMeasure ??
          null
        );
      default:
        return null;
    }
  }

  getCurrency(type: PriceType, initial_price?: boolean): Currency | null {
    switch (type) {
      case PriceType.Net:
        return toCurrency(this.get_prices(initial_price).net?.currency ?? null);
      case PriceType.Sales:
        return toCurrency(this.get_prices(initial_price).sales?.currency ?? null);
      case PriceType.M2:
        return toCurrency(this.get_prices(initial_price).m2?.currency ?? null);
      case PriceType.Line:
        return toCurrency(this.get_prices(initial_price).line?.currency ?? null);
      default:
        return null;
    }
  }

  getCurrencyWithUnit(type: PriceType, initial_price?: boolean): string {
    const currency = this.getCurrency(type, initial_price);
    const unit = this.getBaseUnit(type, initial_price);

    if (!currency || !unit) return '';

    return `${currency}/${unit}`;
  }

  getAttributes(initial_price?: boolean): PriceAttributes {
    if (initial_price && this.initial.attributes) {
      return this.initial.attributes;
    }
    return this.attributes;
  }

  clone(updates?: Partial<PriceAttributes>, initial_price?: boolean): PriceManager {
    const pm = new PriceManager(
      { ...this.attributes, ...(initial_price ? updates : {}) },
      this.get_prices(initial_price),
      this.fetcher
    );
    if (!initial_price && updates) {
      pm.update_attributes(updates);
    }
    return pm;
  }

  getPriceCollection(initial_prices?: boolean): PriceCollection {
    const prices = this.get_prices(initial_prices);

    return {
      attributes: this.getAttributes(initial_prices),
      price_line: prices.line,
      price_m2: prices.m2,
      price_net: prices.net,
      price_sales: prices.sales,
    };
  }
}

class VoidedPriceManager extends PriceManager {
  readonly isVoid = true;

  // Defensive: the void manager is a shared singleton, never mutate it
  set_attributes(): void {}
  update_attributes(): void {}
}

export const VoidPriceManager = new VoidedPriceManager(
  {
    artnr: '',
    address: '',
    customer: '',
    warehouse: '',
    region: '',
    unit: '',
    quantity: 0,
    requestQuantity: 0,
    salespriceUnit: '',
    date: '',
    priceList: '',
    priceListOrigin: '',
  },
  undefined,
  () => Promise.resolve(null)
);

export function toCurrency(currency: Currency | string | null): Currency | null {
  if (currency !== null && currency in Currency) {
    return Currency[currency as keyof typeof Currency];
  }

  return null;
}

function toAmount(amount: number | null): number | null {
  if (amount === null || typeof amount !== 'number') {
    return null;
  }

  return amount;
}

const _precision = 4;
function toCents(amount: number | null, precision: number): number {
  if (amount === null) return 0;

  const str = amount.toFixed(precision);
  const [integer, decimal = ''] = str.split('.');
  const paddedDecimal = decimal.padEnd(precision, '0');

  return parseInt(`${integer}${paddedDecimal}`, 10);
}

function newDinero(amount: number | null, currency: Currency | null): DineroType {
  let cents = 0;
  amount = toAmount(amount);
  if (amount !== null) {
    cents = toCents(amount, _precision);
  }
  return Dinero({
    amount: cents,
    currency: toCurrency(currency as string) || undefined,
    precision: _precision,
  });
}

export class Pricing {
  private d: DineroType = Dinero({ precision: _precision });
  private hasValue: boolean = false;

  set value(amount: number) {
    const currency = this.getCurrency();
    if (currency === null) {
      console.warn("Couldn't set price value because the currency is not recognized.");
      return;
    }
    this.set(amount, currency);
  }
  get value(): number | null {
    if (!this.hasValue) {
      return null;
    }

    return this.d.toUnit();
  }

  /**
   * Creates an instance of the Price class.
   *
   * @param value - The monetary value in major currency units (e.g., 100.50 for 100 dollars and 50 cents).
   * @param currency - The currency of the monetary value (e.g., Currency.USD).
   *
   * @throws Error if the currency is null or undefined.
   *
   * Note: Dinero.js expects the amount in minor currency units (e.g., cents).
   * Therefore, the value is multiplied by 100 to convert from major currency units to minor units.
   * For example, 100.50 (dollars) wcurrencyill be converted to 10050 (cents).
   */
  constructor(amount: number | null, currency: Currency | string | null) {
    // check if amount is a number, otherwise set it to null
    amount = toAmount(amount);
    const _currency = toCurrency(currency as string);

    if (amount !== null && _currency !== null) {
      this.set(amount, _currency);
    }
  }

  set(amount: number, currency: Currency): void {
    this.d = newDinero(amount, currency);
    this.hasValue = true;
  }

  clear(): void {
    this.d = newDinero(null, null);
    this.hasValue = false;
  }

  add(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    const d = amount instanceof Pricing ? amount.d : newDinero(amount, this.getCurrency());
    try {
      if (d.getCurrency() !== this.d.getCurrency()) {
        return;
      }
      return new Pricing(this.d.add(d).toUnit(), this.getCurrency());
    } catch (e) {
      console.warn('Error adding prices', e);
      return;
    }
  }

  subtract(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    const d = amount instanceof Pricing ? amount.d : newDinero(amount, this.getCurrency());
    return new Pricing(this.d.subtract(d).toUnit(), this.getCurrency());
  }

  multiply(amount: Pricing | number): Pricing | void {
    try {
      if (!this.hasValue) return;
      if (amount instanceof Pricing) {
        return new Pricing(this.d.multiply(amount.value || 1).toUnit(), this.getCurrency());
      }
      return new Pricing(this.d.multiply(amount).toUnit(), this.getCurrency());
    } catch (error) {
      console.error('Error multiplying values', {
        currentValue: this.value,
        multiplier: amount instanceof Pricing ? amount.value : amount,
        error,
      });
      return;
    }
  }

  divide(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    if (amount instanceof Pricing) {
      return new Pricing(this.d.divide(amount.value || 1).toUnit(), this.getCurrency());
    }
    return new Pricing(this.d.divide(amount).toUnit(), this.getCurrency());
  }

  /**
   * Formats the price as a string.
   */
  toString(opt?: { currency?: boolean; unit?: string }): string {
    if (!this.hasValue) {
      return '';
    }

    let value = formatCurrency(this.value);

    if (opt?.currency) {
      value += ' ' + this.d.getCurrency();
    }

    if (opt?.unit) {
      value += '/' + opt.unit;
    }

    return value;
  }

  withDiscounts(
    discounts: {
      discount: number;
    }[] = []
  ): Pricing {
    let price = this.d;
    for (const discount of discounts) {
      const discount_amount = price.multiply(discount.discount / 100);
      price = price.subtract(discount_amount);
    }

    return new Pricing(price.toUnit(), this.getCurrency());
  }

  /**
   *
   * The currency of the monetary value (e.g., Currency.USD).
   * If the currency is not recognized, null is returned.
   */
  getCurrency(): Currency | null {
    return toCurrency(this.d.getCurrency());
  }

  asDinero(): DineroType {
    return this.d;
  }
}
